/**
 * The Moon: a sphere far behind the ridges, lit by the sun direction (so phase and terminator orientation come out
 * right by construction), with earthshine on the dark side and atmospheric dimming near the horizon.
 */
import * as THREE from 'three';
import type { Vec3 } from '../frame';
import { sunTransmittance } from './atmosphere';

export const MOON_DISTANCE_M = 45_000;
/** Real angular radius 0.26°, artistically enlarged ×1.6 (brief §4). */
const ANGULAR_RADIUS_DEG = 0.26 * 1.6;

const VERT = /* glsl */ `
varying vec3 vNormal;
varying vec3 vToCam;
void main() {
  vNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
  vToCam = cameraPosition - modelMatrix[3].xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const FRAG = /* glsl */ `
uniform vec3 uSunDir;
uniform vec3 uTint;
uniform float uBrightness;
uniform float uEarthshine;
varying vec3 vNormal;
varying vec3 vToCam;
void main() {
  vec3 nrm = normalize(vNormal);
  float lit = smoothstep(-0.03, 0.08, dot(nrm, uSunDir));
  // maria: soft dark patches placed roughly as on the near side (north up), in disc coordinates facing the camera
  vec3 toCam = normalize(vToCam);
  vec3 right = normalize(cross(vec3(0.0, 1.0, 0.0), toCam));
  vec3 up = cross(toCam, right);
  vec2 d = vec2(dot(nrm, right), dot(nrm, up));
  float dark = 0.0;
  dark += 0.30 * exp(-dot(d - vec2(-0.30, 0.42), d - vec2(-0.30, 0.42)) / 0.045); // Imbrium
  dark += 0.26 * exp(-dot(d - vec2(0.12, 0.38), d - vec2(0.12, 0.38)) / 0.022);   // Serenitatis
  dark += 0.24 * exp(-dot(d - vec2(0.30, 0.12), d - vec2(0.30, 0.12)) / 0.030);   // Tranquillitatis
  dark += 0.26 * exp(-dot(d - vec2(0.64, 0.30), d - vec2(0.64, 0.30)) / 0.010);   // Crisium
  dark += 0.22 * exp(-dot(d - vec2(-0.58, 0.02), d - vec2(-0.58, 0.02)) / 0.080); // Procellarum
  dark += 0.18 * exp(-dot(d - vec2(-0.14, -0.36), d - vec2(-0.14, -0.36)) / 0.030); // Nubium
  dark += 0.16 * exp(-dot(d - vec2(0.52, -0.12), d - vec2(0.52, -0.12)) / 0.018);  // Fecunditatis
  float mare = 1.0 - min(0.45, dark);
  vec3 col = uTint * (uBrightness * lit * mare + uEarthshine);
  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

export class Moon {
  readonly mesh: THREE.Mesh;
  private readonly uniforms = {
    uSunDir: { value: new THREE.Vector3() },
    uTint: { value: new THREE.Color(1, 1, 1) },
    uBrightness: { value: 1 },
    uEarthshine: { value: 0 },
  };

  constructor() {
    const radius = MOON_DISTANCE_M * Math.tan((ANGULAR_RADIUS_DEG * Math.PI) / 180);
    this.mesh = new THREE.Mesh(
      new THREE.SphereGeometry(radius, 48, 24),
      new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms: this.uniforms, depthWrite: false }),
    );
    this.mesh.name = 'moon';
    this.mesh.renderOrder = -90;
    this.mesh.frustumCulled = false;
  }

  /** brightness: HDR radiance of the lit limb before dimming. */
  update(cameraPos: THREE.Vector3, moonDir: Vec3, sunDir: Vec3, moonAltDeg: number, illuminatedFraction: number, brightness: number): void {
    this.mesh.visible = moonAltDeg > -2;
    this.mesh.position.set(...moonDir).multiplyScalar(MOON_DISTANCE_M).add(cameraPos);
    this.uniforms.uSunDir.value.set(...sunDir).normalize();
    const t = sunTransmittance(Math.max(-1, moonAltDeg));
    this.uniforms.uTint.value.setRGB(t[0], t[1], t[2]);
    this.uniforms.uBrightness.value = brightness;
    this.uniforms.uEarthshine.value = brightness * 0.02 * (1 - illuminatedFraction);
  }
}
